import {
  COMPACT_NUMBER_THRESHOLD,
  DEFAULT_NUMBER_MAX_FRACTION_DIGITS,
  SMALL_NUMBER_MAX_FRACTION_DIGITS,
  SMALL_NUMBER_THRESHOLD,
  VERY_SMALL_NUMBER_MAX_FRACTION_DIGITS,
  VERY_SMALL_NUMBER_THRESHOLD,
} from "../constants";

const COMPACT_NUMBER_UNITS = [
  { threshold: 1_000_000_000_000, divisor: 1_000_000_000_000, suffix: "T" },
  { threshold: 1_000_000_000, divisor: 1_000_000_000, suffix: "B" },
  { threshold: 1_000_000, divisor: 1_000_000, suffix: "M" },
  { threshold: 1_000, divisor: 1_000, suffix: "K" },
] as const;

const formatCompactAnalyticsNumber = (value: number) => {
  const absoluteValue = Math.abs(value);
  const unit = COMPACT_NUMBER_UNITS.find(({ threshold }) => absoluteValue >= threshold);

  if (!unit) {
    return value.toLocaleString(undefined, {
      maximumFractionDigits: DEFAULT_NUMBER_MAX_FRACTION_DIGITS,
    });
  }

  const scaledValue = value / unit.divisor;
  const maximumFractionDigits = Math.abs(scaledValue) >= 10 ? 0 : 1;

  return `${scaledValue.toLocaleString(undefined, { maximumFractionDigits })}${unit.suffix}`;
};

export const formatAnalyticsNumber = (value: number) => {
  if (!Number.isFinite(value)) {
    return "";
  }

  const absoluteValue = Math.abs(value);

  if (absoluteValue === 0) {
    return "0";
  }

  if (absoluteValue < VERY_SMALL_NUMBER_THRESHOLD) {
    return value.toLocaleString(undefined, {
      maximumFractionDigits: VERY_SMALL_NUMBER_MAX_FRACTION_DIGITS,
    });
  }

  if (absoluteValue < SMALL_NUMBER_THRESHOLD) {
    return value.toLocaleString(undefined, {
      maximumFractionDigits: SMALL_NUMBER_MAX_FRACTION_DIGITS,
    });
  }

  if (absoluteValue >= COMPACT_NUMBER_THRESHOLD) {
    return formatCompactAnalyticsNumber(value);
  }

  return value.toLocaleString(undefined, {
    maximumFractionDigits: DEFAULT_NUMBER_MAX_FRACTION_DIGITS,
  });
};

export const formatAnalyticsCurrency = (value: number) => {
  if (!Number.isFinite(value)) {
    return "";
  }

  const absoluteValue = Math.abs(value);

  if (absoluteValue === 0) {
    return "$0";
  }

  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits:
      absoluteValue < SMALL_NUMBER_THRESHOLD
        ? SMALL_NUMBER_MAX_FRACTION_DIGITS
        : DEFAULT_NUMBER_MAX_FRACTION_DIGITS,
    notation: absoluteValue >= COMPACT_NUMBER_THRESHOLD ? "compact" : "standard",
  });
};
